import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { HierarchicalCheckboxSelector } from '@openx/components-filters/lib/FiltersBar/components/filterContent/HierarchicalCheckboxSelector.tsx';
import { RadioSelector } from '@openx/components-filters/lib/FiltersBar/components/filterContent/RadioSelector.tsx';
import { useTranslation } from 'react-i18next';
import { type DealPromptFilters, dealPromptFilterDevice, dealPromptFilterFormats } from '../utils/types.ts';

type Props = {
  appliedFilters?: DealPromptFilters;
  onOptionChange: (key: keyof DealPromptFilters, value: string[] | undefined) => void;
  isDisabled?: boolean;
};

const TV_IDS = new Set<string>(['tv_devices', dealPromptFilterDevice.CONNECTED_TV, dealPromptFilterDevice.SET_TOP_BOX]);

const formatsWithDisabledTv = ['banner', 'native'];

const stripTvDevices = (devices: string[]) => devices.filter(id => !TV_IDS.has(id));

export const DealPromptFormatAndDevicesFilter = ({ appliedFilters, onOptionChange, isDisabled }: Props) => {
  const { t } = useTranslation();

  const selectedFormat = appliedFilters?.format?.[0] ?? '';
  const devices = appliedFilters?.ds_device ?? [];

  const isFiltersSelected = !!selectedFormat || !!devices.length;

  const hasFormat = !!selectedFormat;

  const tvDisabled =
    selectedFormat === dealPromptFilterFormats.BANNER || selectedFormat === dealPromptFilterFormats.NATIVE;

  const handleFormatChange = (nextFormatId: string) => {
    onOptionChange('format', nextFormatId ? [nextFormatId] : undefined);

    if (!nextFormatId) {
      onOptionChange('ds_device', undefined);
      return;
    }

    if (nextFormatId === dealPromptFilterFormats.BANNER || nextFormatId === dealPromptFilterFormats.NATIVE) {
      const cleaned = stripTvDevices(devices);
      onOptionChange('ds_device', cleaned.length ? cleaned : undefined);
    }
  };

  const handleDevicesChange = (next: string | string[]) => {
    const nextDevices = Array.isArray(next) ? next : [next];

    if (!hasFormat) {
      onOptionChange('ds_device', undefined);
      return;
    }

    const cleaned = tvDisabled ? stripTvDevices(nextDevices) : nextDevices;
    onOptionChange('ds_device', cleaned.length ? cleaned : undefined);
  };
  const onFilterClear = () => {
    onOptionChange('format', undefined);
    onOptionChange('ds_device', undefined);
  };

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="body1Bold" data-test={'filter-name-label'} data-test-2="filter-name-label-format">
        {t('Format')}
      </Typography>
      <RadioSelector
        options={[
          { id: dealPromptFilterFormats.BANNER, name: t('Banner') },
          { id: dealPromptFilterFormats.VIDEO, name: t('Video') },
          { id: dealPromptFilterFormats.NATIVE, name: t('Native') },
        ]}
        selectedOptions={selectedFormat}
        onOptionChange={handleFormatChange}
        isDisabled={isDisabled}
      />

      <Stack
        sx={[
          {
            gap: 0.5,
          },
          theme => ({
            paddingTop: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }),
        ]}
      >
        <Typography variant="body1Bold" data-test={'filter-name-label'} data-test-2="filter-name-label-format">
          {t('Device')}
        </Typography>

        {!hasFormat && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('Select format to choose devices')}
          </Typography>
        )}

        <HierarchicalCheckboxSelector
          options={[
            { id: dealPromptFilterDevice.DESKTOP, name: t('Desktop'), children: [] },
            {
              id: dealPromptFilterDevice.MOBILE_TABLET,
              name: t('Mobile'),
              children: [
                { id: dealPromptFilterDevice.PHONE, name: t('Smart phone'), children: [] },
                { id: dealPromptFilterDevice.TABLET, name: t('Tablet'), children: [] },
              ],
            },
            {
              id: 'tv_devices',
              name: t('TV Devices'),
              disabled: formatsWithDisabledTv.includes(selectedFormat),
              children: [
                { id: dealPromptFilterDevice.CONNECTED_TV, name: t('Connected TV'), children: [] },
                { id: dealPromptFilterDevice.SET_TOP_BOX, name: t('Set Top Box'), children: [] },
              ],
            },
          ]}
          selectedOptions={devices}
          onOptionChange={handleDevicesChange}
          isDisabled={!!isDisabled || !hasFormat}
        />
      </Stack>

      <Box
        sx={[
          {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          },
          theme => ({
            padding: theme.spacing(1.5, 2, 0),
            borderTop: `1px solid ${theme.palette.divider}`,
          }),
        ]}
      >
        <Button
          variant="text"
          disabled={!isFiltersSelected}
          onClick={onFilterClear}
          sx={{ color: palette.primary.main }}
        >
          {t('Clear filters')}
        </Button>
      </Box>
    </Stack>
  );
};
